/**
 * Alkira Cloud Map - Google Maps Implementation
 */

import {getProviderColor, getMapStyles} from './constants.js';
import {waitForGoogleMaps, parseUrlState, updateUrl} from './utils.js';
import {createMarker, clearMarkers, calculateMarkerOffsets} from './markers.js';
import {setupClusterer} from './clustering.js';
import {createPopupContent, setupNearbyClickHandlers} from './popup.js';
import * as UI from './ui.js';

const CloudMap = {instances: new Map()};

class CloudMapInstance {
    constructor(container) {
        this.container = container;
        this.mapElement = container.querySelector('.cloudmap-map');
        this.mapId = this.mapElement?.id;

        if (!this.mapId || !window.AlkiraCloudMapConfig) {
            console.error('CloudMap: Missing configuration');
            return;
        }

        this.config = window.AlkiraCloudMapConfig;
        this.map = null;
        this.markers = [];
        this.clusterer = null;
        this.infoWindow = null;
        this.data = null;
        this.state = {activeProviders: new Set()};

        // Parse URL state
        const urlState = parseUrlState(this.config.defaults);
        this.state.activeProviders = urlState.activeProviders;
        this.config.defaults.center = urlState.center;
        this.config.defaults.zoom = urlState.zoom;

        this.init();
    }

    async init() {
        try {
            await waitForGoogleMaps();
            this.createMap();
            await this.fetchData();
            this.setupUI();
            this.renderMarkers();
            UI.hideLoading(this.container);
            this.trackEvent('map_view', {providers: Array.from(this.state.activeProviders)});
        } catch (error) {
            console.error('CloudMap: Initialization failed', error);
            UI.showError(this.container, 'Failed to load map. Please try again.');
        }
    }

    createMap() {
        const {googleMaps, defaults} = this.config;

        const mapOptions = {
            center: defaults.center,
            zoom: defaults.zoom,
            mapTypeId: 'roadmap',
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
            gestureHandling: 'greedy',
            styles: getMapStyles(),
        };

        if (googleMaps.mapId) mapOptions.mapId = googleMaps.mapId;

        this.map = new google.maps.Map(this.mapElement, mapOptions);
        this.infoWindow = new google.maps.InfoWindow({maxWidth: 320});

        this.map.addListener('click', () => this.infoWindow.close());

        let moveTimeout;
        this.map.addListener('idle', () => {
            clearTimeout(moveTimeout);
            moveTimeout = setTimeout(() => this.updateUrlState(), 500);
        });
    }

    async fetchData() {
        const response = await fetch(`${this.config.apiBase}/map-data`, {
            headers: {'X-WP-Nonce': this.config.nonce},
        });
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        this.data = await response.json();
    }

    setupUI() {
        UI.setupProviderCards(this.container, this.mapId, this.data, this.state, {
            onToggle: (slug) => this.toggleProvider(slug),
            onClear: () => this.clearFilters(),
        });

        UI.setupSuperRegions(this.container, this.mapId, (region) => {
            this.map.panTo(region.center);
            this.map.setZoom(region.zoom);
            this.trackEvent('super_region_zoom', {region: region.name});
        });

        UI.setupLegend(this.container, this.mapId, this.data?.providers);
        UI.updateClearFiltersVisibility(this.container, this.mapId, this.state.activeProviders, this.data?.providers?.length || 0);
    }

    toggleProvider(slug) {
        if (this.state.activeProviders.has(slug)) {
            if (this.state.activeProviders.size > 1) {
                this.state.activeProviders.delete(slug);
            }
        } else {
            this.state.activeProviders.add(slug);
        }

        UI.updateCardStates(this.container, this.state.activeProviders);
        this.renderMarkers();
        this.updateUrlState();
        UI.updateClearFiltersVisibility(this.container, this.mapId, this.state.activeProviders, this.data?.providers?.length || 0);
        this.trackEvent('filter_provider', {provider: slug, active: this.state.activeProviders.has(slug)});
    }

    clearFilters() {
        this.state.activeProviders.clear();
        this.data.providers.forEach(p => this.state.activeProviders.add(p.slug));

        UI.updateCardStates(this.container, this.state.activeProviders);
        this.renderMarkers();
        this.updateUrlState();
        UI.updateClearFiltersVisibility(this.container, this.mapId, this.state.activeProviders, this.data?.providers?.length || 0);
        this.trackEvent('clear_filters', {});
    }

    renderMarkers() {
        clearMarkers(this.markers, this.clusterer);
        this.markers = [];

        if (!this.data?.regions?.features) {
            UI.updateCount(this.container, 0, this.config.i18n);
            return;
        }

        // Filter to visible features first
        const visibleFeatures = this.data.regions.features.filter(
            feature => this.state.activeProviders.has(feature.properties.provider.slug)
        );

        // Calculate offsets for overlapping markers
        const offsets = calculateMarkerOffsets(visibleFeatures);

        visibleFeatures.forEach((feature, index) => {
            const {coordinates} = feature.geometry;
            const props = feature.properties;

            const providerWithColor = {
                ...props.provider,
                color: getProviderColor(props.provider.slug),
            };

            // Use offset position if available, otherwise original
            const position = offsets.has(index)
                ? offsets.get(index)
                : {lat: coordinates[1], lng: coordinates[0]};

            const marker = createMarker({
                position,
                provider: providerWithColor,
                data: {...props, provider: providerWithColor, latitude: coordinates[1], longitude: coordinates[0]},
                map: this.map,
                config: this.config,
                onOpen: (marker, data) => this.openInfoWindow(marker, data),
            });

            if (marker) {
                this.markers.push(marker);
            }
        });

        this.clusterer = setupClusterer(this.map, this.markers, this.config, this.clusterer);
        UI.updateCount(this.container, visibleFeatures.length, this.config.i18n);
        UI.announce(this.container, this.mapId, `${visibleFeatures.length} ${visibleFeatures.length === 1 ? 'region' : 'regions'} displayed`);
    }

    openInfoWindow(marker, data) {
        this.infoWindow.setContent(createPopupContent(data, this.data, this.state.activeProviders));

        if (marker.position) {
            this.infoWindow.setPosition(marker.position);
            this.infoWindow.open(this.map);
        } else {
            this.infoWindow.open(this.map, marker);
        }

        setupNearbyClickHandlers(this.infoWindow, this.map);
        this.trackEvent('marker_click', {region: data.code, provider: data.provider.slug});
    }

    updateUrlState() {
        updateUrl(this.map, this.state.activeProviders, this.data?.providers?.length || 0);
    }

    trackEvent(eventName, eventData = {}) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({event: `cloudmap_${eventName}`, cloudmap: eventData});
        document.dispatchEvent(new CustomEvent('cloudmap:event', {detail: {name: eventName, data: eventData}}));
    }
}

function initCloudMaps() {
    document.querySelectorAll('.alkira-cloudmap-container').forEach(container => {
        const mapId = container.querySelector('.cloudmap-map')?.id;
        if (mapId && !CloudMap.instances.has(mapId)) {
            CloudMap.instances.set(mapId, new CloudMapInstance(container));
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCloudMaps);
} else {
    initCloudMaps();
}

window.AlkiraCloudMap = CloudMap;
